/**
 * How often upload progress is allowed to reach React, and how often it is mirrored to storage.
 *
 * tus calls `onProgress` from `xhr.upload.onprogress`, which the XHR spec caps at one event per
 * 50ms — so ~20/s per in-flight request — and uploads run in parallel via `Promise.all`, so the
 * aggregate is ~20 x N. Each event wrote two React states AND triggered a synchronous
 * localStorage read-parse-merge-stringify-write. On an Android WebView that saturates the main
 * thread: scrolling stutters for seconds and typing falls behind (#2722).
 *
 * The transport is not the problem and is untouched. What is bounded here is how often that
 * progress is published.
 *
 * DESKTOP IS DELIBERATELY UNCHANGED. Both decisions return exactly today's behaviour when
 * `isWebView` is false — desktop absorbs the waste without a user noticing, and a live product
 * is not the place to find out otherwise. The gate is the caller's; these functions only make
 * the two branches explicit and testable, which is the one thing this repo's vitest setup
 * (node env, `*.logic.test.ts` only) can actually verify.
 */

/** Percent change required before a progress tick is published in a WebView. */
export const WEBVIEW_PROGRESS_STEP_PERCENT = 5;

/** Minimum gap between storage mirrors of a progress-only change, in a WebView. */
export const WEBVIEW_PERSIST_MIN_INTERVAL_MS = 1000;

/**
 * Whether this progress value should be published to React.
 *
 * `lastEmitted` is null before the first emit.
 *
 * 100 is ALWAYS published, whatever the step: the final tick is what flips a row out of its
 * uploading state, and a bound that can swallow it would leave a bar parked at 96% on a
 * finished upload. Same for a value that went backwards, which a resumed upload can produce —
 * suppressing it would show progress that never corrects.
 */
export const shouldEmitProgress = ({
  lastEmitted,
  next,
  isWebView,
}: {
  lastEmitted: number | null;
  next: number;
  isWebView: boolean;
}): boolean => {
  if (!isWebView) return true;
  if (lastEmitted === null) return true;
  if (next >= 100) return true;
  if (next < lastEmitted) return true;
  return next - lastEmitted >= WEBVIEW_PROGRESS_STEP_PERCENT;
};

/**
 * Whether the persisted-uploads mirror should be written now.
 *
 * MEMBERSHIP CHANGES ARE NEVER DEFERRED. This mirror is the only thing that publishes a newly
 * created row, and a row that never reaches storage has no resume record and leaves its
 * IndexedDB blob orphaned — so adding or removing a row always flushes immediately, and only a
 * progress-only change is throttled. That distinction is the whole reason this is a function
 * rather than a timer.
 */
export type PersistRow = { progress?: number };

/**
 * Everything about the rows EXCEPT progress.
 *
 * Comparing ids alone was not enough, and the gap lost data rather than frames: a resume
 * rewrites `serverId`, `uploadUrl` and `uploadToken` on the SAME row id, and the dead-URL
 * cleanup clears those same fields. Both read as "ids unchanged", so both were deferred — and
 * with no trailing write, an upload that then failed left storage holding a dead URL and no
 * serverId. `use-retry-failed-upload` reads that store directly, so the retry went to the dead
 * URL: exactly the lost write the merge in media-upload-context exists to prevent.
 *
 * Signature-based rather than field-list-based on purpose. A field added to PersistedUpload
 * later is covered automatically; a list would silently keep deferring the new one.
 */
export const persistSignature = <T extends PersistRow>(rows: readonly T[]): string =>
  JSON.stringify(rows.map(({ progress: _progress, ...rest }) => rest));

export const shouldFlushPersist = ({
  prevSignature,
  nextSignature,
  lastFlushAt,
  now,
  isWebView,
}: {
  /** From persistSignature, for the rows last WRITTEN (not merely last seen). */
  prevSignature: string;
  nextSignature: string;
  lastFlushAt: number | null;
  now: number;
  isWebView: boolean;
}): boolean => {
  if (!isWebView) return true;

  // Anything but a progress change is published immediately: a new row, a removed row, a
  // reorder, and every durable field a resume rewrites.
  if (prevSignature !== nextSignature) return true;

  if (lastFlushAt === null) return true;
  return now - lastFlushAt >= WEBVIEW_PERSIST_MIN_INTERVAL_MS;
};
